import { useGetLocale, useTranslate } from '@refinedev/core';
import { Card, Col, Descriptions, Row, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { ContentPublisherOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { countryName } from '@/utils/countries';
import { formatDateTime } from '@/utils/format';

interface PublisherGeneralTabProps {
  record: ContentPublisherOut;
}

export const PublisherGeneralTab = ({ record }: PublisherGeneralTabProps) => {
  const t = useTranslate();
  const locale = useGetLocale()() ?? 'pl';
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const tf = (field: string) => t(`content_catalog.publishers.fields.${field}`);

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: record.id },
    {
      key: 'external_id',
      label: tf('external_id'),
      children: <Typography.Text code>{record.external_id}</Typography.Text>,
    },
    { key: 'name', label: tf('name'), children: record.name },
    {
      key: 'legal_name',
      label: tf('legal_name'),
      children: orDash(record.legal_name),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const contactItems: DescriptionsProps['items'] = [
    { key: 'email', label: tf('email'), children: orDash(record.email) },
    {
      key: 'telephone',
      label: tf('telephone'),
      children: orDash(record.telephone),
    },
    {
      key: 'street_address',
      label: tf('street_address'),
      children: orDash(record.street_address),
    },
    {
      key: 'postal_code',
      label: tf('postal_code'),
      children: orDash(record.postal_code),
    },
    {
      key: 'location_name',
      label: tf('location_name'),
      children: orDash(record.location_name),
    },
    {
      key: 'country',
      label: tf('country'),
      children: orDash(
        record.country_code && countryName(record.country_code, locale)
      ),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Card title={t('base.sections.basic_info')}>
          <Descriptions
            {...verticalDescProps}
            column={{ xs: 1, sm: 2, md: 3 }}
            items={basicItems}
          />
        </Card>
      </Col>
      <Col flex="1 1 360px">
        <Card title={t('content_catalog.publishers.sections.contact')}>
          <Descriptions
            {...verticalDescProps}
            column={{ xs: 1, sm: 2 }}
            items={contactItems}
          />
        </Card>
      </Col>
    </Row>
  );
};
